import { motion } from 'framer-motion';
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Users, Clock } from 'lucide-react';
import Button from '@mui/material/Button';
import AdventureImg from '../Images/adventure.jpg';
import CulturalImg from '../Images/cultural.jpg';
import RelaxationImg from '../Images/relaxation.jpg';
import WildlifeImg from '../Images/wildlife.jpg';
import { useState } from 'react';

function TourCard({ title, description, image, features, price, index }) {
  const [isHovered, setIsHovered] = useState(false);
const navigate = useNavigate();
  return (
    <motion.div
      className="relative group"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ delay: index * 0.2, duration: 0.6, type: "spring" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.div
        className="relative bg-white rounded-3xl shadow-xl overflow-hidden h-full"
        whileHover={{ y: -15, rotateX: 5, rotateY: 5, transition: { duration: 0.3 } }}
      >
        {/* Hover effect border with blue-light blue gradient */}
        <motion.div
          className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20"
          style={{
            background: 'linear-gradient(135deg, rgba(59,130,246,0.5), rgba(147,197,253,0.5))',
            padding: '3px',
          }}
        />
        {/* Shine effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-200/30 to-transparent z-10 pointer-events-none"
          initial={{ x: '-100%' }}
          animate={isHovered ? { x: '200%' } : { x: '-100%' }}
          transition={{ duration: 0.8 }}
        />
        {/* Image section */}
        <div className="relative h-64 overflow-hidden">
          <motion.div whileHover={{ scale: 1.15 }} transition={{ duration: 0.6 }}>
            <img src={image} alt={title} className="w-full h-64 object-cover" />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 via-blue-600/20 to-transparent" />
          {/* Rating badge */}
          <motion.div
            className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg"
            initial={{ opacity: 0, scale: 0 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2 + 0.3, type: "spring" }}
            whileHover={{ scale: 1.1 }}
          >
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-blue-500 fill-blue-400" />
              <span className="text-sm text-blue-700">4.9</span>
            </div>
          </motion.div>
          {/* Title overlay */}
          <motion.div
            className="absolute bottom-4 left-4 right-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2 + 0.4 }}
          >
            <h3 className="text-3xl text-white drop-shadow-lg">{title}</h3>
          </motion.div>
        </div>

        <div className="p-6 relative">
          <motion.p
            className="text-blue-900 mb-6 text-base"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2 + 0.5 }}
          >
            {description}
          </motion.p>
          <motion.div
            className="flex gap-4 mb-6 pb-6 border-b border-blue-200"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2 + 0.6 }}
          >
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <Clock className="w-4 h-4 text-blue-500" />
              <span>3-5 Days</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-blue-600">
              <Users className="w-4 h-4 text-blue-500" />
              <span>Group Size: Up to 12 People</span>
            </div>
          </motion.div>
          <ul className="space-y-3 mb-6">
            {features.map((feature, i) => (
              <motion.li
                key={i}
                className="flex items-center text-sm text-blue-800"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 + 0.6 + i * 0.1 }}
                whileHover={{ x: 5 }}
              >
                <motion.div
                  className="w-2 h-2 bg-gradient-to-r from-blue-500 to-blue-300 rounded-full mr-3"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{
                    delay: index * 0.2 + 0.6 + i * 0.1,
                    type: "spring",
                    stiffness: 500
                  }}
                  whileHover={{ scale: 1.5 }}
                />
                {feature}
              </motion.li>
            ))}
          </ul>
          <motion.div
            className="flex items-center justify-between mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.2 + 0.8 }}
          >
            <div>
              <div className="text-sm text-blue-400">Starting from</div>
              <div className="text-3xl bg-gradient-to-r from-blue-500 to-blue-300 bg-clip-text text-transparent">
                ₹{price.toLocaleString('en-IN')}
              </div>
            </div>
            <motion.div animate={isHovered ? { rotate: 45 } : { rotate: 0 }} transition={{ duration: 0.3 }}>
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-100 to-blue-200 flex items-center justify-center">
                <ArrowRight className="w-6 h-6 text-blue-500" />
              </div>
            </motion.div>
          </motion.div>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.2 }}>
     <Button
    onClick={() => {
      navigate(`/packages?category=${encodeURIComponent(title.toLowerCase())}`);
    }}
    variant="contained"
    color="primary"
    className="w-full bg-gradient-to-r from-blue-600 to-blue-400 hover:from-blue-700 hover:to-blue-500 shadow-lg py-6 text-base"
    sx={{
      color: 'white',
      backgroundImage: 'linear-gradient(to right, #2563eb, #60a5fa)',
      '&:hover': {
        backgroundImage: 'linear-gradient(to right, #1d4ed8, #3b82f6)'
      }
    }}
  >
    <span>Explore {title} Tours</span>
    <motion.div animate={isHovered ? { x: 5 } : { x: 0 }} transition={{ duration: 0.2 }} className="inline-block ml-2">
      <ArrowRight className="w-5 h-5" />
    </motion.div>
  </Button>
          </motion.div>
        </div>
        {/* Decorative corner accent */}
        <motion.div
          className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-400/20 via-blue-200/20 to-transparent rounded-bl-full"
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.2 + 0.2, type: "spring" }}
        />
      </motion.div>
    </motion.div>
  );
}

export default function ExploreTourPackages() {
 const tours = [
    {
    title: "Relaxation",
    description: "Rejuvenate with spa treatments, guided yoga sessions, pristine beaches, and peaceful nature walks.",
    image: RelaxationImg,
    price: 9000,
    features: [
      "Daily yoga & meditation classes",
      "Spa therapies & massages",
      "Exclusive private beach access",
      "Nutritious gourmet meals"
    ]
  },
  {
    title: "Adventure",
    description: "Pulse-raising activities including river rafting, trekking, zip-lining, and starlit camping for thrill seekers.",
    image: AdventureImg,
    price: 12000,
    features: [
      "River rafting & zip-lining",
      "Expert guided treks & hikes",
      "Overnight camping under stars",
      "Wildlife observation walks"
    ]
  },
  {
    title: "Cultural",
    description: "Deep dive into local heritage with guided monument tours, artisan workshops, cooking classes, and vibrant folk shows.",
    image: CulturalImg,
    price: 9500,
    features: [
      "Guided heritage & monument tours",
      "Traditional artisan workshops",
      "Hands-on regional cooking classes",
      "Live folk dance & music performances"
    ]
  },

  {
    title: "Wildlife",
    description: "Explore wildlife sanctuaries with jungle safaris, birdwatching tours, expert photography guidance, and eco camping.",
    image: WildlifeImg,
    price: 11000,
    features: [
      "Jungle & night safaris",
      "Guided birdwatching expeditions",
      "Wildlife photography sessions",
      "Camping in protected reserves"
    ]
  }
];


  return (
    <section className="py-16 px-4 md:px-6 lg:px-8 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto mb-12 text-center">
        <h2 className="mb-4 text-4xl font-bold text-blue-900">Explore Our Tour Packages</h2>
        <p className="max-w-xl mx-auto text-lg text-blue-800">
          Handcrafted itineraries offering unforgettable experiences across adventure, culture, relaxation, and wildlife tours. Choose your perfect journey below.
        </p>
      </div>
      <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {tours.map((tour, i) => (
          <TourCard
            key={tour.title}
            title={tour.title}
            description={tour.description}
            image={tour.image}
            features={tour.features}
            price={tour.price}
            index={i}
          />
        ))}
      </div>
    </section>
  );
}
